// ---------------------------------------------------------------------------------------------
// HER REVIEW CAROUSEL — **THE GOLD RAIL BESIDE HER ARTWORK.**
//
// Her order: *"the REVIEW CAROUSEL scrolling vertically in gold on the black side rail with the gold
// dividers framing her art."*
//
// >>> WHERE IT LIVES WAS A MEASUREMENT RATHER THAN A CHOICE (R25/R180). <<< Measured at 1280, 1366,
// 1440, 1920 and 2560: her artwork begins at x=250 on every one of them, because `main` carries the
// shell's `padding-left: 250px` sidebar gutter and HOME-1 zeroed `body`'s padding rather than
// `main`'s. **So the black side rail is not something this component invents — it is dead space that
// has been standing on the front door since her artwork shipped.** The black BELOW her art is
// 140-230px at those widths and is NEGATIVE on an ultrawide, so the side rail is the only black that
// exists at every desktop width; a carousel in a band that disappears on some screens is a carousel
// nobody sees. On a PHONE there is no gutter at all and her vertical art leaves the black below it,
// which is where the phone carousel goes.
//
// >>> THE LAW THIS ROUND TURNS ON: A STRANGER IS NEVER TOLD SOMETHING BROKE. <<< This sits on the
// page her advertising pays to bring people to, so its four states are NOT the usual four (R18/R64):
// *still reading* HOLDS STILL, *the read failed* draws NOTHING AT ALL, *genuinely empty* draws
// nothing, and only *here they are* paints. **A rail that says "reviews could not be loaded" to a
// stranger is worse than a rail that says nothing** — it is the house reporting its own fault on the
// first screen somebody ever sees, in exchange for information she cannot act on. The failure is
// still a fact and is still LOGGED; what it may not do is reach her.
//
// **EVERY WORD IT RENDERS WAS COMPOSED BY `reviewQuote` ON THE SERVER**, which is the one composer
// the membership page reads too -- so a member is quoted identically on both surfaces and the
// quotation marks are the law's rather than three stylesheets' (R28/R29). This file does not call it
// and must not: see the type below for what the second call cost.
import { useEffect, useState } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/**
 * >>> WHAT ARRIVES IS ALREADY COMPOSED, AND THAT IS THE POINT (R28/R29). <<<
 *
 * `reviewQuote` is the ONE composer -- the quotation marks and the first name are a fact about the
 * quote rather than a fact about three stylesheets -- and `/api/reviews` runs it SERVER-SIDE, so
 * the membership page, this rail and anything later quote a member identically. **The first draft of
 * this component ran it again on the way in and rendered NOTHING**, because a composed row carries no
 * `body` for a second pass to read: a second composer that silently produces an empty surface, which
 * is exactly the shape that law exists to refuse. The rail RENDERS; it does not decide wording.
 */
export type ReviewRow = { quote?: string; name?: string; provisional?: boolean };

/**
 * >>> HOW MANY QUOTES A RAIL MAY HOLD, AND WHY IT IS BOUNDED. <<<
 *
 * The scroll is a CSS animation over a duplicated list, so its duration has to be proportional to
 * the content or a long list crawls and a short one races. Bounding the list is what keeps that
 * arithmetic honest -- and it is her front door, so an unbounded read would put every approved
 * review this house ever collects into the first paint.
 */
export const RAIL_MAX = 12;

/** Seconds per quote. Slow on purpose: this is read out of the corner of an eye, not scanned. */
export const RAIL_SECONDS_PER_QUOTE = 7;

/**
 * >>> FOUR STATES, AND ONLY ONE OF THEM PAINTS (R18). <<<
 *
 * `rows === undefined` is a read still in flight; `null` is a read that ARRIVED AND COULD NOT BE
 * MADE; `[]` is a genuinely empty set. All three draw nothing, and they are kept apart anyway,
 * because collapsing them is how a later round comes to render a failure at a stranger.
 */
export function railState(rows?: ReviewRow[] | null): "waiting" | "failed" | "empty" | "list" {
  if (rows === undefined) return "waiting";
  if (rows === null) return "failed";
  return rows.length ? "list" : "empty";
}

/** The composed quotes, capped, with anything the composer refuses dropped rather than drawn blank. */
export function railQuotes(rows?: ReviewRow[] | null) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r && typeof r.quote === "string" && r.quote.trim())
    .slice(0, RAIL_MAX)
    .map((r, i) => ({ key: `${i}`, quote: String(r.quote).trim(), name: typeof r.name === "string" ? r.name : "" }));
}

/**
 * The scroll's duration. **DERIVED from the count** rather than a constant, so three quotes and
 * twelve read at the same pace -- a fixed duration makes a long rail unreadable and a short one
 * frantic, and neither has any symptom but a stranger looking away.
 */
export function railDuration(count: number) {
  return Math.max(RAIL_SECONDS_PER_QUOTE * 2, count * RAIL_SECONDS_PER_QUOTE);
}

export function ReviewRail({ rows, place = "band", reduceMotion }: {
  rows?: ReviewRow[] | null;
  // LANDING v3 added "band": her horizontal strip across the top of the landing, scrolling right to
  // left. It is a PLACE rather than a second component, so the approved pool, REV-1's privacy law,
  // the reduced-motion ruling, the seamless double-draw and the silent-failure law all reach it by
  // EXISTING (R28/R29) -- and a second scroller would be a second answer to which quotes a stranger
  // may see, on the one page her advertising pays for.
  //
  // >>> `rail` AND `below` ARE RETIRED WITH THE ARTWORK THAT RENDERED THEM, AND THE DEFAULT MOVED
  // WITH THEM. <<< Those two were HOME-1's gutter rail and its phone counterpart, drawn only by
  // `HomeArtwork`, which LANDING v3 orphaned and this round DELETED -- so their rules are gone from
  // the sheet. A `place` naming a rule that no longer exists has NO ERROR STATE: the aside simply
  // renders with no position, no width and no divider, exactly as `.button--quiet` did on her plum
  // panel and `--xs` did on an avatar (INSTALL-1, ITEM 8). **A default is the same defect wearing a
  // parameter list**, which is why it is `band` rather than a name with nothing behind it.
  place?: "band" | "talk";
  // Handed in so a gate drives both directions without a browser (R46's locked-door shape).
  reduceMotion?: boolean;
}) {
  const prefers = usePrefersReducedMotion();
  const still = reduceMotion === undefined ? prefers : reduceMotion;
  const quotes = railQuotes(rows);
  if (railState(rows) !== "list" || !quotes.length) return null;

  // >>> THE LIST IS DRAWN TWICE AND THE SECOND COPY IS HIDDEN FROM A SCREEN READER. <<< A seamless
  // vertical scroll needs the sequence to repeat, and without `aria-hidden` a member listening to
  // this page hears every review twice with no idea why.
  const seconds = railDuration(quotes.length);
  const marquee = !still;

  return (
    <aside className={`review-rail review-rail--${place}`} aria-label="What members say">
      <div
        className={marquee ? "review-rail__track is-scrolling" : "review-rail__track"}
        style={marquee ? { animationDuration: `${seconds}s` } : undefined}
      >
        {quotes.map((q) => (
          <figure className="review-rail__item" key={`a-${q.key}`}>
            <blockquote className="review-rail__quote">{q.quote}</blockquote>
            {q.name ? <figcaption className="review-rail__name">{q.name}</figcaption> : null}
          </figure>
        ))}
        {marquee
          ? quotes.map((q) => (
              <figure className="review-rail__item" key={`b-${q.key}`} aria-hidden="true">
                <blockquote className="review-rail__quote">{q.quote}</blockquote>
                {q.name ? <figcaption className="review-rail__name">{q.name}</figcaption> : null}
              </figure>
            ))
          : null}
      </div>
    </aside>
  );
}

/**
 * >>> THE READ, AND ITS REFUSAL IS SILENT BY DESIGN. <<<
 *
 * `/api/reviews` is a public read that buys nothing and serves only what `showableReviews` allows --
 * approved rows, plus provisional rows from PUBLIC surfaces, with her DM privacy law held twice (in
 * the SQL and again in JS). A failure answers `null`, which `railState` reads as `failed` and the
 * rail draws nothing.
 */
export function useReviews(enabled: boolean, { curated = false }: { curated?: boolean } = {}) {
  const [rows, setRows] = useState<ReviewRow[] | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    // HER RULING: the front door asks for the curated set. Every other surface is byte-identical,
    // because the default is false (ITEM 12's neutrality argument).
    fetch(curated ? "/api/reviews?curated=1" : "/api/reviews")
      .then((r) => (r.ok ? r.json() : null))
      // `ok: false` is the door saying the READ failed, which is not the same fact as an empty
      // house (R18). Both draw nothing, so nothing a member sees turns on it -- what turns on it is
      // whether a later round can tell them apart.
      .then((d) => { if (live) setRows(d && d.ok !== false && Array.isArray(d.reviews) ? d.reviews : null); })
      .catch(() => { if (live) setRows(null); });
    return () => { live = false; };
  }, [enabled, curated]);
  return rows;
}
